/*
This file is located in the main branch
Last time the file was updated (UTC): [11.12.2024 08:50:34]
Permissions to update outside the main branch: [false]
*/

import {
    AccordionBody,
    Button,
    Draggable,
    DropDown,
    useErrorState,
    ValidatedDynamicList,
    ValidatedDynamicListFormComponentProps,
    ValidatedInput,
} from '@finbeat/react-toolkit';
import { faPlus, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getSelectList } from 'axios/dictionaryApi';
import useApi from 'hooks/useApi';
import { TradeOrgContact, TradeOrgContactFace } from 'models/api/tradeOrg';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Accordion, AccordionHeader, AccordionItem, Col, Row } from 'reactstrap';
import { TradeOrgAddForm } from './TradeOrgChangeModal';
import { TradeOrgContactsForm, TradeOrgContactsFormWrapper } from './TradeOrgContacts';

const contactFaceEmptyItem: TradeOrgContactFace = {
    contacts: [
        {
            id: null,
            type: '',
            value: '',
        },
    ],
    dsc: '',
    name: '',
};

const TradeOrgChangeModalTabContactFaces = ({ formHasRole }: { formHasRole: boolean }) => {
    const { handleRequestError } = useErrorState();
    const { i18n } = useTranslation();
    const api = useApi();

    const [open, setOpen] = useState('');

    const { fields, append, remove, swap } = useFieldArray<TradeOrgAddForm>({
        name: 'contactFaces',
    });

    const { isFetching, data: contactTypes } = useQuery(
        ['ContactTypes', i18n.language],
        () => getSelectList(api, ['ContactTypes'], i18n.language),
        {
            onError: handleRequestError,
        }
    );

    const contactFaceAdd = () => {
        append(contactFaceEmptyItem, { shouldFocus: true });
    };

    const contactFaceDelete = (idx: number) => {
        remove(idx);
    };

    const reorderContactFaces = (source: number, target: number) => {
        swap(source, target);
    };

    const toggle = (id: string) => setOpen((prev) => (prev === id ? '' : id));

    const accordionProps = {
        open: open,
        toggle: toggle,
    };

    return (
        <>
            <Row>
                <Col>
                    <Button
                        className="my-3 float-end"
                        color="secondary"
                        onClick={contactFaceAdd}
                        type="button"
                        disabled={!formHasRole}
                    >
                        <FontAwesomeIcon icon={faPlus} /> <Trans i18nKey="BackOfficeAdd" />
                    </Button>
                </Col>
            </Row>
            <div className={`tradeOrg-modal__content`}>
                {
                    <Accordion {...accordionProps}>
                        {fields.map(({ id }, idx) => (
                            <Draggable<number>
                                key={id}
                                typeKey="contactFaces"
                                sourceKey={idx}
                                onItemReorder={reorderContactFaces}
                            >
                                <AccordionItem>
                                    <ContactFaceForm
                                        idx={idx}
                                        contactTypes={
                                            contactTypes ? contactTypes['ContactTypes'] : []
                                        }
                                        contactTypesIsFetching={isFetching}
                                        formHasRole={formHasRole}
                                        contactFaceDelete={contactFaceDelete}
                                        id={id}
                                        setOpen={setOpen}
                                    />
                                </AccordionItem>
                            </Draggable>
                        ))}
                    </Accordion>
                }
            </div>
        </>
    );
};

const ContactFaceForm = ({
    idx,
    contactTypes,
    contactTypesIsFetching,
    formHasRole,
    contactFaceDelete,
    id,
    setOpen,
}: {
    idx: number;
    contactTypes: DropDown[];
    contactTypesIsFetching: boolean;
    formHasRole: boolean;
    contactFaceDelete: (idx: number) => void;
    id: string;
    setOpen: Dispatch<SetStateAction<string>>;
}) => {
    const { t } = useTranslation();

    const { watch } = useFormContext<TradeOrgAddForm>();
    const name = watch(`contactFaces.${idx}.name`);

    const formComponent = useCallback(
        (props: ValidatedDynamicListFormComponentProps) => (
            <TradeOrgContactsForm
                {...props}
                idxRoot={idx}
                contactTypes={contactTypes}
                contactTypesIsFetching={contactTypesIsFetching}
                formHasRole={formHasRole}
            />
        ),
        [idx, formHasRole, contactTypes, contactTypesIsFetching]
    );

    return (
        <>
            <AccordionHeader tag="div" targetId={id}>
                <div>
                    {name && name.length ? name : `${t('BackOfficeNewContact')} ${idx + 1}`}
                    {formHasRole && (
                        <FontAwesomeIcon
                            icon={faTrashCan}
                            onClick={(e) => {
                                e.stopPropagation();
                                contactFaceDelete(idx);
                            }}
                            className="text-danger"
                        />
                    )}
                </div>
            </AccordionHeader>
            <AccordionBody accordionId={id} toggle={(val) => val && setOpen(val.toString())}>
                <ValidatedInput<TradeOrgAddForm>
                    className="mb-3"
                    label={t('BackOfficeName')}
                    name={`contactFaces.${idx}.name`}
                    disabled={!formHasRole}
                />
                <ValidatedDynamicList<TradeOrgAddForm, TradeOrgContact>
                    formComponent={formComponent}
                    wrapper={TradeOrgContactsFormWrapper}
                    emptyRecord={{
                        id: null,
                        type: '',
                        value: '',
                    }}
                    name={`contactFaces.${idx}.contacts`}
                    compareFunc={(a, b) => a?.type === b?.type}
                    prepend={false}
                    disabled={!formHasRole}
                />
                <ValidatedInput<TradeOrgAddForm>
                    label={t('BackOfficeDescription')}
                    name={`contactFaces.${idx}.dsc`}
                    type="textarea"
                    disabled={!formHasRole}
                />
            </AccordionBody>
        </>
    );
};

export default TradeOrgChangeModalTabContactFaces;
